/**
 * Tenant-wide timesheet lock settings — the shared contract between the
 * API (source of truth) and the web client (which previews the next lock
 * date as the operator edits the form before saving).
 *
 * Dates are ISO YYYY-MM-DD strings computed in UTC so the result never
 * drifts with the server's local timezone. This module is dependency-free
 * (shared only depends on zod elsewhere); keep it that way so it can be
 * imported from both the Bun API and the browser bundle.
 *
 * See `docs/timesheet-settlement.md` for the two-stage close this models.
 */

export const TIMESHEET_LOCK_PERIODS = ['weekly', 'monthly'] as const;
export type TimesheetLockPeriod = (typeof TIMESHEET_LOCK_PERIODS)[number];

/** Narrow an unknown (e.g. a raw DB text column) to a known lock period. */
export function isTimesheetLockPeriod(value: unknown): value is TimesheetLockPeriod {
  return typeof value === 'string' && (TIMESHEET_LOCK_PERIODS as readonly string[]).includes(value);
}

/**
 * WHICH of a period's two closing boundaries is being computed. A period
 * closes in two stages — the accounting soft close, then the hard close:
 *
 *   - `submission` — the submission deadline. Past it, no new time enters
 *     the period: no create, no editing or deleting a draft, no submitting
 *     a draft, no recall.
 *   - `settlement` — the hard close, `settleDays` further working days on.
 *     Between the two, only work already committed to the period may be
 *     finished: approve, reject, reverse an approval, and correct and
 *     resubmit an entry that was already submitted once and bounced.
 *
 * The axis is deliberately WHAT AN ACTION DOES, not who performs it — a
 * submitter acts inside the settlement window too (fixing a rejection), so
 * "who" would be the wrong discriminator. The stage for a given mutation is
 * a function of `(action, row status)`; see `timesheetLockStageFor` in
 * `./timesheet-lock-stage`.
 *
 * `submission` is the default everywhere, so the stricter of the two
 * boundaries is what you get by omission.
 *
 * The settlement window is a superset of the submission one (the day counts
 * are additive and non-negative), so no action ever loses time it had under
 * a single-boundary rule.
 */
export const TIMESHEET_LOCK_STAGES = ['submission', 'settlement'] as const;
export type TimesheetLockStage = (typeof TIMESHEET_LOCK_STAGES)[number];

export interface TimesheetLockSettings {
  lockPeriod: TimesheetLockPeriod;
  /**
   * Working days of grace after the period ends, during which its
   * timesheets stay open to new time — the SUBMISSION DEADLINE.
   * Non-negative integer. The period closes at the start of the next
   * working day after the last grace day: `2` keeps a Mon–Sun week open
   * through Tuesday and closes it Wednesday; `0` closes it on the first
   * working day after the period ends.
   *
   * Weekends in the window are free — they never consume a grace day, so
   * a month ending on a Friday with `0` stays open across that weekend
   * and closes the Monday.
   */
  graceDays: number;
  /**
   * FURTHER working days granted to SETTLEMENT only, counted from where
   * the submission window ends — the settlement boundary is
   * `graceDays + settleDays` working days after the period ends.
   * Non-negative integer.
   *
   * Additive rather than absolute so the two settings can't be configured
   * into an incoherent pair: settlement can never get LESS time than the
   * submission window it closes out, which would leave entries submitted
   * on the last grace day permanently undecidable.
   *
   * Optional, defaulting to 0 — a bare `{ lockPeriod, graceDays }` is the
   * single-boundary configuration and keeps its exact behavior, including
   * for `stage: 'settlement'`. Production resolves it explicitly (see
   * `TimesheetSettingsService.resolveLockState`); the default only covers
   * literals that predate the field.
   */
  settleDays?: number;
}

/**
 * The full lock configuration as resolved from a tenant's settings —
 * the period/grace cadence plus the master on/off switch. When
 * `lockEnabled` is false, periods never auto-close regardless of the
 * other fields.
 */
export interface TimesheetLockState extends TimesheetLockSettings {
  lockEnabled: boolean;
}

/** Today in UTC as an ISO YYYY-MM-DD string. */
export function todayUtcIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * "Today" as an ISO YYYY-MM-DD string in the given IANA timezone (e.g.
 * `America/Los_Angeles`). This is what the lock boundary keys on: a
 * period must close at *local* midnight for the timesheet owner, not at
 * UTC midnight — otherwise a user west of UTC loses up to a day of their
 * window and the client (local) and server (UTC) disagree right at the
 * boundary.
 *
 * `now` is injectable for deterministic tests; it defaults to the current
 * instant. An unknown/invalid timezone falls back to UTC rather than
 * throwing — `users.timezone` is validated on write, so this only guards
 * against corrupt data. Uses `en-CA` because it formats as YYYY-MM-DD.
 */
export function todayInZone(timeZone: string, now: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

/**
 * The next date a timesheet period closes, given the lock settings and a
 * reference date `asOf` (ISO YYYY-MM-DD, defaults to today UTC). Returns
 * the earliest closing date that is on or after `asOf`.
 *
 *   - weekly: periods are Mon–Sun. The period ends on Sunday and closes on
 *     the working day after its `graceDays` grace working days.
 *   - monthly: periods are calendar months. The period ends on the last
 *     day of the month and closes the same way.
 *
 * See `lockDateForDate` for the exact offset (`graceDays + 1` business
 * days) and why the `+ 1` is there. `stage` selects WHICH boundary is
 * wanted — `settlement` adds the further `settleDays` on top.
 *
 * ## Why this has to look BACKWARDS
 *
 * It is tempting to answer with the closing date of the period containing
 * `asOf` and stop — that date is always strictly after `asOf`, so it looks
 * like "the next one". It is not, as soon as a window overhangs into the
 * following period: an EARLIER period can still be open, and its deadline
 * lands sooner.
 *
 * Weekly, `graceDays: 0`, `settleDays: 3`, `asOf` 2026-08-26. The period
 * containing today (Aug 24–30) settles on 2026-09-03. But Aug 17–23 has not
 * settled yet either — it settles 2026-08-27, tomorrow. Answering 09-03
 * tells an approver they have a week when they have a day.
 *
 * So walk back while the previous period is STILL open and take the
 * earliest such deadline. Closing dates are monotonic in the period (a later
 * period end can only produce a later closing date), so the first closed
 * period ends the walk — everything before it is closed too.
 */
export function computeNextLockDate(
  settings: TimesheetLockSettings,
  asOf: string = todayUtcIso(),
  stage: TimesheetLockStage = 'submission',
): string {
  let earliest = lockDateForDate(settings, asOf, stage);
  let cursor = asOf;
  // Bounded by construction: each step moves a whole period earlier, and the
  // window spans at most 365 + 365 + 1 business days (the validator's cap on
  // each setting), so no more than ~150 weekly periods can still be open.
  // The cap is belt — the `<= asOf` break is what actually terminates.
  for (let i = 0; i < 400; i++) {
    const previous = addDays(startOfPeriod(settings, cursor), -1);
    const previousLock = lockDateForDate(settings, toIso(previous), stage);
    // Locking is inclusive of the lock date, so `<= asOf` means that period
    // has already closed — and by monotonicity so has every period before it.
    if (previousLock <= asOf) break;
    earliest = previousLock;
    cursor = toIso(previous);
  }
  return earliest;
}

/** First day of the period containing `date` — Monday for weekly, the 1st
 *  for monthly. The mirror of the `endOfWeek`/`endOfMonth` pair that
 *  `lockDateForDate` uses. */
function startOfPeriod(settings: TimesheetLockSettings, date: string): Date {
  const d = parseIsoUtc(date);
  if (settings.lockPeriod === 'weekly') return addDays(endOfWeek(d), -6);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

/**
 * The first date on which the period *containing* `date` is closed for
 * `stage` — i.e. the day *after* the last of that stage's working days
 * following the period's end (Sunday for weekly, last calendar day for
 * monthly). Both `date` and the result are ISO YYYY-MM-DD in UTC.
 *
 * The period closes in two stages: `submission` (the default) uses
 * `graceDays`, `settlement` uses `graceDays + settleDays`. Callers state
 * which boundary they mean; omitting it yields the earlier, stricter one,
 * so a forgotten stage can only ever be conservative.
 *
 * Grace days are counted in business days: weekends are skipped as the
 * days are tallied, so a 2-day window always means two working days,
 * never a Saturday/Sunday.
 *
 * The `+ 1` is what makes a grace day a *whole* day. Enforcement is
 * inclusive of this date (`asOf >= lockDate`, see `isTimesheetDateLocked`),
 * so the returned day is the first one on which the action is refused, and
 * the window therefore runs to the end of the preceding working day.
 * Without it, `graceDays: 2` would close at midnight *starting* the second
 * working day and grant only one day in practice — and `graceDays: 0`
 * would close a period during its own final day.
 *
 * Because the day counts are `>= 0`, the offset is always >= 1, and
 * `addBusinessDays` only ever lands on a weekday for a positive count —
 * so the closing date is inherently a business day and needs no separate
 * weekend roll-forward.
 *
 * Unlike `computeNextLockDate`, `date` can be in the past: this is the
 * closing date for whatever period the given entry falls in, which is what
 * enforcement (`isTimesheetDateLocked`) keys on.
 */
export function lockDateForDate(
  settings: TimesheetLockSettings,
  date: string,
  stage: TimesheetLockStage = 'submission',
): string {
  const d = parseIsoUtc(date);
  const periodEnd = settings.lockPeriod === 'weekly' ? endOfWeek(d) : endOfMonth(d);
  return toIso(addBusinessDays(periodEnd, graceDaysFor(settings, stage) + 1));
}

/**
 * Working days the given stage gets after a period ends. `submission` gets
 * `graceDays`; `settlement` gets that PLUS `settleDays`, so its window
 * always contains the submission one rather than competing with it.
 *
 * A missing/negative `settleDays` contributes nothing — the field is
 * optional for callers that predate it, and the validator already bounds it
 * on the way in, so this only guards against corrupt stored data.
 */
export function graceDaysFor(settings: TimesheetLockSettings, stage: TimesheetLockStage): number {
  if (stage !== 'settlement') return settings.graceDays;
  return settings.graceDays + Math.max(0, settings.settleDays ?? 0);
}

/** One row of the upcoming-close-dates preview: a period and when it closes. */
export interface UpcomingLockPeriod {
  /** First day of the period (Monday for weekly, the 1st for monthly). */
  periodStart: string;
  /** Last day of the period (Sunday for weekly, last calendar day for monthly). */
  periodEnd: string;
  /** The SUBMISSION DEADLINE — first day the period accepts no new time,
   *  the day after the last of its grace days. */
  lockDate: string;
  /** The HARD CLOSE — first day the period is closed to settlement too,
   *  the day after the last of the further `settleDays` working days.
   *  Equal to `lockDate` when no settlement period is configured. */
  settleDate: string;
}

/**
 * `count` periods with BOTH their closing dates — the schedule the settings
 * page previews so an operator can confirm the cadence before saving. All
 * dates are ISO YYYY-MM-DD in UTC.
 *
 * Starts at the earliest period that still has ANY deadline pending, which
 * is NOT always the period containing `asOf`. Once a window overhangs into
 * the next period, an earlier period is still settling, and starting at
 * "today's period" would drop it from the table entirely — hiding the very
 * deadline that is about to bite. See `computeNextLockDate` for the worked
 * example.
 *
 * The settlement window is the wider of the two, so it decides where the
 * table starts: any period still open for submission is still open for
 * settlement, never the reverse. A row can therefore carry a `lockDate` that
 * has already passed while its `settleDate` has not — that row is precisely
 * the one worth showing.
 *
 * Both dates are computed for every row rather than behind a flag: the
 * preview's whole job is to let an operator see how far apart the two
 * deadlines land before committing to them.
 */
export function upcomingLockDates(
  settings: TimesheetLockSettings,
  count: number,
  asOf: string = todayUtcIso(),
): UpcomingLockPeriod[] {
  const periods: UpcomingLockPeriod[] = [];
  // Walk back to the earliest period whose SETTLEMENT deadline is still
  // pending; same monotonicity argument as `computeNextLockDate`.
  let start = asOf;
  for (let i = 0; i < 400; i++) {
    const previous = toIso(addDays(startOfPeriod(settings, start), -1));
    if (lockDateForDate(settings, previous, 'settlement') <= asOf) break;
    start = previous;
  }
  let cursor = parseIsoUtc(start);
  for (let i = 0; i < count; i++) {
    const periodEnd = settings.lockPeriod === 'weekly' ? endOfWeek(cursor) : endOfMonth(cursor);
    const periodStart =
      settings.lockPeriod === 'weekly'
        ? addDays(periodEnd, -6)
        : new Date(Date.UTC(periodEnd.getUTCFullYear(), periodEnd.getUTCMonth(), 1));
    periods.push({
      periodStart: toIso(periodStart),
      periodEnd: toIso(periodEnd),
      // Delegate rather than re-deriving: the preview and enforcement must
      // never disagree about where the boundary falls.
      lockDate: lockDateForDate(settings, toIso(periodEnd), 'submission'),
      settleDate: lockDateForDate(settings, toIso(periodEnd), 'settlement'),
    });
    cursor = addDays(periodEnd, 1);
  }
  return periods;
}

/**
 * Whether the period containing `entryDate` is closed for `stage` as of
 * `asOf` (both ISO YYYY-MM-DD). The settlement window extends past the
 * submission one by `settleDays`, so the same cell can be closed to new
 * time and still open to a correction or a decision.
 *
 * Locking is inclusive of the closing date: a period is closed for the whole
 * of `lockDateForDate(...)`, which is the day after that stage's last
 * working day — so the window ends at the midnight closing the preceding
 * working day. This matches the closing schedule the settings page previews.
 *
 * `asOf` is the reference "today", and because the comparison is `>=`, an
 * EARLIER `asOf` is more permissive. Callers pass a local today from
 * `todayInZone(user.timezone)` so the boundary closes at local midnight
 * rather than UTC's. Where the acting user differs from the timesheet
 * owner (an approver deciding someone else's entry), the server passes
 * `min(owner today, acting today)` so neither side of a cross-timezone
 * pair loses part of their final day — see `TimesheetLockGuard` in the
 * API, which owns that rule. The `/timesheets` page evaluates the owner's
 * today alone, which is the same value: every path it gates acts AS the
 * owner. Defaults to UTC today only as a fallback for contexts with no
 * user.
 *
 * Returns false whenever `lockEnabled` is off — periods never auto-close
 * until an operator opts in. ISO date strings compare correctly with `>=`
 * (lexicographic order == chronological order for zero-padded YYYY-MM-DD).
 */
export function isTimesheetDateLocked(
  settings: TimesheetLockState,
  entryDate: string,
  asOf: string = todayUtcIso(),
  stage: TimesheetLockStage = 'submission',
): boolean {
  if (!settings.lockEnabled) return false;
  return asOf >= lockDateForDate(settings, entryDate, stage);
}

// ---- Pure UTC date helpers -------------------------------------------------

function parseIsoUtc(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d));
}

function toIso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

/**
 * Add `days` business days to `date`, stepping over weekends as the days
 * are counted: each grace day advances to the next weekday (Mon–Fri), and
 * Saturdays/Sundays in the window do not consume a grace day. So two grace
 * days past a Thursday lands on the following Tuesday (Fri, then Mon), not
 * Saturday.
 *
 * With `days === 0` the input is returned unchanged — it may still be a
 * weekend (e.g. a weekly period's Sunday end). Every `days >= 1` call
 * lands on a weekday, because the count only advances when the cursor
 * steps onto one; `lockDateForDate` relies on that to guarantee a
 * business-day closing date without a separate weekend roll-forward.
 *
 * Exported for direct unit testing; takes and returns UTC `Date`s and has
 * no other intended callers outside this module.
 */
export function addBusinessDays(date: Date, days: number): Date {
  let result = date;
  let added = 0;
  while (added < days) {
    result = addDays(result, 1);
    const day = result.getUTCDay();
    if (day !== 0 && day !== 6) added++;
  }
  return result;
}

/** Sunday ending the Mon–Sun week containing `date` (getUTCDay: Sun=0). */
function endOfWeek(date: Date): Date {
  const day = date.getUTCDay();
  const daysUntilSunday = day === 0 ? 0 : 7 - day;
  return addDays(date, daysUntilSunday);
}

/** Last calendar day of the month containing `date`. */
function endOfMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0));
}
